import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Building2, Store, MapPin, Star, Filter, ArrowUpRight, ArrowDownRight, CheckCircle2, XCircle, Package, Coffee, Fish, Beef, Gift, Wine, Truck, Car, Mail, Leaf, QrCode, CreditCard, Salad } from 'lucide-react';
import { KPICard } from '../components/ui/KPICard';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ScatterChart, Scatter, ZAxis } from 'recharts';
// Service definitions
const services = [{
  id: 'lidl_plus',
  name: 'Lidl Plus',
  icon: CreditCard
}, {
  id: 'backshop',
  name: 'Backshop',
  icon: Coffee
}, {
  id: 'non_food',
  name: 'Non-Food Aktionen',
  icon: Gift
}, {
  id: 'fleisch',
  name: 'Frisches Fleisch',
  icon: Beef
}, {
  id: 'fisch',
  name: 'Frischer Fisch',
  icon: Fish
}, {
  id: 'getraenke',
  name: 'Getränke',
  icon: Wine
}, {
  id: 'parkplaetze',
  name: 'Parkplätze',
  icon: Car
}, {
  id: 'bio_organic',
  name: 'Bio Organic',
  icon: Leaf
}, {
  id: 'scan_go',
  name: 'Scan & Go',
  icon: QrCode
}];
// Market data with services
const marketsData = [{
  id: 'hamburg-hafencity',
  name: 'Lidl XL Hamburg-Hafencity',
  region: 'Deutschland',
  rating: 4.3,
  size: 2800,
  services: ['lidl_plus', 'backshop', 'non_food', 'fleisch', 'fisch', 'getraenke', 'parkplaetze', 'bio_organic', 'scan_go']
}, {
  id: 'berlin-kreuzberg',
  name: 'Lidl Berlin-Kreuzberg',
  region: 'Deutschland',
  rating: 3.8,
  size: 1200,
  services: ['lidl_plus', 'backshop', 'non_food', 'parkplaetze']
}, {
  id: 'muenchen-schwabing',
  name: 'Lidl XL München-Schwabing',
  region: 'Deutschland',
  rating: 4.6,
  size: 1800,
  services: ['lidl_plus', 'backshop', 'non_food', 'fleisch', 'fisch', 'getraenke', 'parkplaetze', 'bio_organic', 'scan_go']
}, {
  id: 'koeln-ehrenfeld',
  name: 'Lidl Express Köln-Ehrenfeld',
  region: 'Deutschland',
  rating: 3.5,
  size: 1100,
  services: ['lidl_plus', 'backshop', 'non_food']
}, {
  id: 'frankfurt-sachsenhausen',
  name: 'Lidl XL Frankfurt-Sachsenhausen',
  region: 'Deutschland',
  rating: 4.4,
  size: 1600,
  services: ['lidl_plus', 'backshop', 'non_food', 'fleisch', 'fisch', 'getraenke', 'parkplaetze', 'bio_organic', 'scan_go']
}, {
  id: 'dortmund-innenstadt',
  name: 'Lidl Dortmund-Innenstadt',
  region: 'Deutschland',
  rating: 3.9,
  size: 1400,
  services: ['lidl_plus', 'backshop', 'non_food', 'fleisch', 'getraenke', 'parkplaetze']
}, {
  id: 'leipzig-sued',
  name: 'Lidl XL Leipzig-Süd',
  region: 'Deutschland',
  rating: 4.5,
  size: 2200,
  services: ['lidl_plus', 'backshop', 'non_food', 'fleisch', 'fisch', 'getraenke', 'parkplaetze', 'bio_organic', 'scan_go']
}, {
  id: 'stuttgart-west',
  name: 'Lidl Stuttgart-West',
  region: 'Deutschland',
  rating: 4.2,
  size: 1500,
  services: ['lidl_plus', 'backshop', 'non_food', 'fleisch', 'getraenke', 'parkplaetze', 'bio_organic']
}, {
  id: 'freiburg-zentrum',
  name: 'Lidl XL Freiburg',
  region: 'Deutschland',
  rating: 4.1,
  size: 1350,
  services: ['lidl_plus', 'backshop', 'non_food', 'fleisch', 'getraenke', 'parkplaetze', 'bio_organic']
}, {
  id: 'kiel-zentrum',
  name: 'Lidl XL Kiel',
  region: 'Deutschland',
  rating: 4.4,
  size: 1650,
  services: ['lidl_plus', 'backshop', 'non_food', 'fleisch', 'fisch', 'getraenke', 'parkplaetze', 'bio_organic', 'scan_go']
}, {
  id: 'hannover-list',
  name: 'Lidl XL Hannover-List',
  region: 'Deutschland',
  rating: 4.0,
  size: 1300,
  services: ['lidl_plus', 'backshop', 'non_food', 'fleisch', 'getraenke', 'parkplaetze']
}, {
  id: 'nuernberg-zentrum',
  name: 'Lidl XL Nürnberg-Zentrum',
  region: 'Deutschland',
  rating: 4.3,
  size: 1700,
  services: ['lidl_plus', 'backshop', 'non_food', 'fleisch', 'fisch', 'getraenke', 'parkplaetze', 'bio_organic', 'scan_go']
}, {
  id: 'dresden-altstadt',
  name: 'Lidl XL Dresden-Altstadt',
  region: 'Deutschland',
  rating: 4.1,
  size: 1250,
  services: ['lidl_plus', 'backshop', 'non_food', 'fleisch', 'getraenke', 'parkplaetze', 'bio_organic']
}, {
  id: 'duesseldorf-bilk',
  name: 'Lidl XL Düsseldorf-Bilk',
  region: 'Deutschland',
  rating: 4.2,
  size: 1550,
  services: ['lidl_plus', 'backshop', 'non_food', 'fleisch', 'fisch', 'getraenke', 'parkplaetze', 'bio_organic', 'scan_go']
}, {
  id: 'bremen-mitte',
  name: 'Lidl Express Bremen-Mitte',
  region: 'Deutschland',
  rating: 3.9,
  size: 1150,
  services: ['lidl_plus', 'backshop', 'non_food', 'fleisch', 'parkplaetze']
}];
// Calculate service impact on rating
const calculateServiceImpact = () => {
  return services.map(service => {
    const marketsWithService = marketsData.filter(m => m.services.includes(service.id));
    const marketsWithoutService = marketsData.filter(m => !m.services.includes(service.id));
    const avgWithService = marketsWithService.length > 0 ? marketsWithService.reduce((acc, m) => acc + m.rating, 0) / marketsWithService.length : 0;
    const avgWithoutService = marketsWithoutService.length > 0 ? marketsWithoutService.reduce((acc, m) => acc + m.rating, 0) / marketsWithoutService.length : 0;
    return {
      ...service,
      avgRatingWith: avgWithService,
      avgRatingWithout: avgWithoutService,
      impact: avgWithService - avgWithoutService,
      marketsCount: marketsWithService.length,
      penetration: marketsWithService.length / marketsData.length * 100
    };
  }).sort((a, b) => b.impact - a.impact);
};
const serviceImpactData = calculateServiceImpact();
// Level options
const levels = [{
  id: 'services',
  name: 'Service-Analyse',
  icon: Package
}, {
  id: 'markt',
  name: 'Markt-Übersicht',
  icon: Store
}];
export function ServicesReputationPage() {
  const [selectedLevel, setSelectedLevel] = useState<'services' | 'markt'>('services');
  const [selectedService, setSelectedService] = useState<string | null>(null);
  // Calculate overall stats
  const avgRating = marketsData.reduce((acc, m) => acc + m.rating, 0) / marketsData.length;
  const avgServices = marketsData.reduce((acc, m) => acc + m.services.length, 0) / marketsData.length;
  // Markets sorted by service count
  const marketsByServices = [...marketsData].sort((a, b) => b.services.length - a.services.length);
  const topServiceMarkets = marketsByServices.slice(0, 3);
  const lowServiceMarkets = marketsByServices.slice(-3).reverse();
  // Correlation between service count and rating
  const correlationData = marketsData.map(m => ({
    name: m.name,
    serviceCount: m.services.length,
    rating: m.rating,
    z: m.size
  }));
  // Calculate correlation
  const n = correlationData.length;
  const sumX = correlationData.reduce((acc, d) => acc + d.serviceCount, 0);
  const sumY = correlationData.reduce((acc, d) => acc + d.rating, 0);
  const sumXY = correlationData.reduce((acc, d) => acc + d.serviceCount * d.rating, 0);
  const sumX2 = correlationData.reduce((acc, d) => acc + d.serviceCount * d.serviceCount, 0);
  const sumY2 = correlationData.reduce((acc, d) => acc + d.rating * d.rating, 0);
  const correlation = (n * sumXY - sumX * sumY) / Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));
  // Top impact services
  const topImpactServices = serviceImpactData.filter(s => s.impact > 0).slice(0, 5);
  return <div className="p-6 space-y-6 overflow-y-auto h-[calc(100vh-64px)]" data-id="element-1211">
      {/* Level Filter */}
      <Card className="!p-4" data-id="element-1212">
        <div className="flex items-center gap-4 flex-wrap" data-id="element-1213">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-1214">
            <Filter size={16} data-id="element-1215" />
            <span data-id="element-1216">Ansicht:</span>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-1217">
            {levels.map(level => {
            const isSelected = selectedLevel === level.id;
            const Icon = level.icon;
            return <button key={level.id} onClick={() => setSelectedLevel(level.id as any)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${isSelected ? 'bg-lidl-blue text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-1218">
                  <Icon size={14} data-id="element-1219" />
                  {level.name}
                </button>;
          })}
          </div>
        </div>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1220">
        <KPICard title="Korrelation Services ↔ Rating" value={correlation.toFixed(2)} change={correlation > 0.6 ? 'Stark positiv' : correlation > 0.3 ? 'Moderat' : 'Schwach'} trend="up" icon={<TrendingUp size={18} data-id="element-1222" />} subtext="Pearson-Koeffizient" data-id="element-1221" />
        <KPICard title="Ø Services pro Markt" value={avgServices.toFixed(1)} change={`von ${services.length} möglich`} trend="up" icon={<Package size={18} data-id="element-1224" />} subtext="Durchschnitt" data-id="element-1223" />
        <KPICard title="Top Service-Effekt" value={`+${topImpactServices[0]?.impact.toFixed(2) || 0}★`} change={topImpactServices[0]?.name || '-'} trend="up" icon={<Star size={18} data-id="element-1226" />} subtext="Rating-Unterschied" data-id="element-1225" />
        <KPICard title="Ø Bewertung" value={avgRating.toFixed(2)} change="+0.15" trend="up" icon={<Star size={18} data-id="element-1228" />} subtext="Google Rating" data-id="element-1227" />
      </div>

      {selectedLevel === 'services' ? <>
          {/* Service Count vs Rating Scatter - Full Width */}
          <Card data-id="element-1229">
            <div className="p-6" data-id="element-1230">
              <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-1231">
                Anzahl Services vs. Rating
              </h3>
              <p className="text-sm text-gray-500 mb-4" data-id="element-1232">
                Korrelation zwischen Service-Angebot und Bewertung
              </p>

              <div className="h-72" data-id="element-1233">
                <ResponsiveContainer width="100%" height="100%" data-id="element-1234">
                  <ScatterChart margin={{
                top: 20,
                right: 20,
                bottom: 20,
                left: 20
              }} data-id="element-1235">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-1236" />
                    <XAxis type="number" dataKey="serviceCount" name="Services" domain={[0, 15]} axisLine={false} tickLine={false} tick={{
                  fontSize: 11,
                  fill: '#9ca3af'
                }} label={{
                  value: 'Anzahl Services',
                  position: 'bottom',
                  offset: 0,
                  style: {
                    fontSize: 11,
                    fill: '#6b7280'
                  }
                }} data-id="element-1237" />
                    <YAxis type="number" dataKey="rating" name="Rating" domain={[3.2, 5]} axisLine={false} tickLine={false} tick={{
                  fontSize: 11,
                  fill: '#9ca3af'
                }} label={{
                  value: 'Rating',
                  angle: -90,
                  position: 'insideLeft',
                  style: {
                    fontSize: 11,
                    fill: '#6b7280'
                  }
                }} data-id="element-1238" />
                    <ZAxis type="number" dataKey="z" range={[100, 500]} data-id="element-1239" />
                    <Tooltip contentStyle={{
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  fontSize: '12px'
                }} content={({
                  active,
                  payload
                }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    return <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-100" data-id="element-1241">
                              <p className="font-medium text-gray-900 text-sm mb-2" data-id="element-1242">
                                {data.name.replace('Lidl ', '').replace('XL ', '')}
                              </p>
                              <div className="space-y-1 text-xs" data-id="element-1243">
                                <p className="text-gray-600" data-id="element-1244">
                                  <span className="font-medium" data-id="element-1245">Services:</span>{' '}
                                  {data.serviceCount}
                                </p>
                                <p className="text-gray-600" data-id="element-1246">
                                  <span className="font-medium" data-id="element-1247">Rating:</span>{' '}
                                  {data.rating.toFixed(1)} ★
                                </p>
                              </div>
                            </div>;
                  }
                  return null;
                }} data-id="element-1240" />
                    <Scatter name="Märkte" data={correlationData} fill="#0050AA" data-id="element-1248">
                      {correlationData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.serviceCount >= avgServices ? '#639754' : '#DF0000'} fillOpacity={0.7} data-id="element-1249" />)}
                    </Scatter>
                  </ScatterChart>
                </ResponsiveContainer>
              </div>

              <div className="mt-4 p-3 bg-blue-50 rounded-lg" data-id="element-1250">
                <div className="text-sm text-blue-800" data-id="element-1251">
                  <strong data-id="element-1252">
                    Korrelation: {(correlation * 100).toFixed(0)}%
                  </strong>{' '}
                  – Märkte mit mehr Services haben tendenziell bessere
                  Bewertungen.
                </div>
              </div>
            </div>
          </Card>

          {/* Service Impact Chart */}
          <Card data-id="element-1253">
            <div className="p-6" data-id="element-1254">
              <div className="flex items-center justify-between mb-6" data-id="element-1255">
                <div data-id="element-1256">
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-1257">
                    Service-Auswirkung auf Rating
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-1258">
                    Durchschnittliches Rating: Mit Service vs. Ohne Service
                  </p>
                </div>
              </div>

              <div className="h-[500px]" data-id="element-1259">
                <ResponsiveContainer width="100%" height="100%" data-id="element-1260">
                  <BarChart data={serviceImpactData} layout="vertical" margin={{
                top: 5,
                right: 30,
                left: 140,
                bottom: 5
              }} data-id="element-1261">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={true} vertical={false} data-id="element-1262" />
                    <XAxis type="number" domain={[3, 5]} axisLine={false} tickLine={false} tick={{
                  fontSize: 11,
                  fill: '#9ca3af'
                }} data-id="element-1263" />
                    <YAxis type="category" dataKey="name" axisLine={false} tickLine={false} tick={{
                  fontSize: 12,
                  fill: '#374151'
                }} width={130} data-id="element-1264" />
                    <Tooltip contentStyle={{
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  fontSize: '12px'
                }} formatter={(value: number, name: string) => [value.toFixed(2) + ' ★', name === 'avgRatingWith' ? 'Mit Service' : 'Ohne Service']} data-id="element-1265" />
                    <Bar dataKey="avgRatingWith" name="Mit Service" fill="#639754" radius={[0, 4, 4, 0]} barSize={16} data-id="element-1266" />
                    <Bar dataKey="avgRatingWithout" name="Ohne Service" fill="#DF0000" radius={[0, 4, 4, 0]} barSize={16} data-id="element-1267" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-center gap-8 text-sm" data-id="element-1268">
                <div className="flex items-center gap-2" data-id="element-1269">
                  <div className="w-3 h-3 rounded" style={{
                backgroundColor: '#639754'
              }} data-id="element-1270"></div>
                  <span className="text-gray-600" data-id="element-1271">Mit Service</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-1272">
                  <div className="w-3 h-3 rounded" style={{
                backgroundColor: '#DF0000'
              }} data-id="element-1273"></div>
                  <span className="text-gray-600" data-id="element-1274">Ohne Service</span>
                </div>
              </div>
            </div>
          </Card>

          {/* Top Impact Services - Full Width */}
          <Card data-id="element-1275">
            <div className="p-6" data-id="element-1276">
              <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2" data-id="element-1277">
                <ArrowUpRight className="text-green-600" size={20} data-id="element-1278" />
                Höchster Rating-Effekt
              </h3>
              <p className="text-sm text-gray-500 mb-4" data-id="element-1279">
                Services mit größtem positiven Einfluss auf Bewertungen
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3" data-id="element-1280">
                {topImpactServices.map((service, index) => {
              const ServiceIcon = service.icon;
              return <motion.div key={service.id} initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: index * 0.1
              }} className="flex flex-col items-center p-4 rounded-lg text-center" style={{
                backgroundColor: '#e8f0e5'
              }} data-id="element-1281">
                      <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-3" style={{
                  backgroundColor: '#639754'
                }} data-id="element-1282">
                        <ServiceIcon size={24} className="text-white" data-id="element-1283" />
                      </div>
                      <div className="font-medium text-gray-900 text-sm mb-1" data-id="element-1284">
                        {service.name}
                      </div>
                      <div className="font-bold text-xl" style={{
                  color: '#639754'
                }} data-id="element-1285">
                        +{service.impact.toFixed(2)} ★
                      </div>
                      <div className="text-xs text-gray-500 mt-1" data-id="element-1286">
                        {service.marketsCount} Märkte (
                        {service.penetration.toFixed(0)}%)
                      </div>
                    </motion.div>;
            })}
              </div>
            </div>
          </Card>

          {/* Service Penetration Table */}
          <Card data-id="element-1287">
            <div className="p-6" data-id="element-1288">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1289">
                Service-Übersicht
              </h3>

              <div className="overflow-x-auto" data-id="element-1290">
                <table className="w-full text-sm" data-id="element-1291">
                  <thead data-id="element-1292">
                    <tr className="border-b border-gray-200" data-id="element-1293">
                      <th className="text-left py-3 px-4 font-medium text-gray-500" data-id="element-1294">
                        Service
                      </th>
                      <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1295">
                        Märkte
                      </th>
                      <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1296">
                        Penetration
                      </th>
                      <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1297">
                        Ø Rating mit
                      </th>
                      <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1298">
                        Ø Rating ohne
                      </th>
                      <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1299">
                        Effekt
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-1300">
                    {serviceImpactData.map(service => {
                  const ServiceIcon = service.icon;
                  return <tr key={service.id} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-1301">
                          <td className="py-3 px-4" data-id="element-1302">
                            <div className="flex items-center gap-2" data-id="element-1303">
                              <ServiceIcon size={16} className="text-gray-400" data-id="element-1304" />
                              <span className="font-medium text-gray-900" data-id="element-1305">
                                {service.name}
                              </span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-right text-gray-900" data-id="element-1306">
                            {service.marketsCount}
                          </td>
                          <td className="py-3 px-4 text-right" data-id="element-1307">
                            <div className="flex items-center justify-end gap-2" data-id="element-1308">
                              <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-1309">
                                <div className="h-full rounded-full" style={{
                            width: `${service.penetration}%`,
                            backgroundColor: '#0050AA'
                          }} data-id="element-1310" />
                              </div>
                              <span className="text-gray-500 w-10 text-right" data-id="element-1311">
                                {service.penetration.toFixed(0)}%
                              </span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-right font-medium text-gray-900" data-id="element-1312">
                            {service.avgRatingWith.toFixed(2)} ★
                          </td>
                          <td className="py-3 px-4 text-right text-gray-500" data-id="element-1313">
                            {service.avgRatingWithout.toFixed(2)} ★
                          </td>
                          <td className="py-3 px-4 text-right" data-id="element-1314">
                            <span className={`font-medium ${service.impact > 0 ? 'text-green-600' : service.impact < 0 ? 'text-red-600' : 'text-gray-500'}`} data-id="element-1315">
                              {service.impact > 0 ? '+' : ''}
                              {service.impact.toFixed(2)} ★
                            </span>
                          </td>
                        </tr>;
                })}
                  </tbody>
                </table>
              </div>
            </div>
          </Card>
        </> : <>
          {/* Market View */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1316">
            {/* Top Service Markets */}
            <Card data-id="element-1317">
              <div className="p-6" data-id="element-1318">
                <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2" data-id="element-1319">
                  <ArrowUpRight className="text-green-600" size={20} data-id="element-1320" />
                  Märkte mit meisten Services
                </h3>
                <p className="text-sm text-gray-500 mb-4" data-id="element-1321">
                  Höchstes Service-Angebot
                </p>

                <div className="space-y-3" data-id="element-1322">
                  {topServiceMarkets.map((market, index) => <motion.div key={market.id} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: index * 0.1
              }} className="flex items-center justify-between p-3 rounded-lg" style={{
                backgroundColor: '#e8f0e5'
              }} data-id="element-1323">
                      <div className="flex items-center gap-3" data-id="element-1324">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{
                    backgroundColor: '#639754'
                  }} data-id="element-1325">
                          {index + 1}
                        </div>
                        <div data-id="element-1326">
                          <div className="font-medium text-gray-900 text-sm" data-id="element-1327">
                            {market.name}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-1328">
                            {market.region}
                          </div>
                        </div>
                      </div>
                      <div className="text-right" data-id="element-1329">
                        <div className="font-bold text-lg" style={{
                    color: '#639754'
                  }} data-id="element-1330">
                          {market.services.length} Services
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-1331">
                          {market.rating.toFixed(1)} ★
                        </div>
                      </div>
                    </motion.div>)}
                </div>
              </div>
            </Card>

            {/* Low Service Markets */}
            <Card data-id="element-1332">
              <div className="p-6" data-id="element-1333">
                <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2" data-id="element-1334">
                  <ArrowDownRight className="text-red-600" size={20} data-id="element-1335" />
                  Märkte mit wenigsten Services
                </h3>
                <p className="text-sm text-gray-500 mb-4" data-id="element-1336">
                  Potenzial für Service-Erweiterung
                </p>

                <div className="space-y-3" data-id="element-1337">
                  {lowServiceMarkets.map((market, index) => <motion.div key={market.id} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: index * 0.1
              }} className="flex items-center justify-between p-3 rounded-lg" style={{
                backgroundColor: '#fde8e8'
              }} data-id="element-1338">
                      <div className="flex items-center gap-3" data-id="element-1339">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{
                    backgroundColor: '#DF0000'
                  }} data-id="element-1340">
                          !
                        </div>
                        <div data-id="element-1341">
                          <div className="font-medium text-gray-900 text-sm" data-id="element-1342">
                            {market.name}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-1343">
                            {market.region}
                          </div>
                        </div>
                      </div>
                      <div className="text-right" data-id="element-1344">
                        <div className="font-bold text-lg" style={{
                    color: '#DF0000'
                  }} data-id="element-1345">
                          {market.services.length} Services
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-1346">
                          {market.rating.toFixed(1)} ★
                        </div>
                      </div>
                    </motion.div>)}
                </div>

                <div className="mt-4 p-3 bg-blue-50 rounded-lg" data-id="element-1347">
                  <div className="text-sm text-blue-800" data-id="element-1348">
                    <strong data-id="element-1349">Empfehlung:</strong> Diese Märkte könnten von
                    zusätzlichen Services wie Backshop, Non-Food oder Scan & Go
                    profitieren.
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Market Service Matrix */}
          <Card data-id="element-1350">
            <div className="p-6" data-id="element-1351">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1352">
                Service-Matrix nach Markt
              </h3>

              <div className="overflow-x-auto" data-id="element-1353">
                <table className="w-full text-sm" data-id="element-1354">
                  <thead data-id="element-1355">
                    <tr className="border-b border-gray-200" data-id="element-1356">
                      <th className="text-left py-3 px-4 font-medium text-gray-500 sticky left-0 bg-white" data-id="element-1357">
                        Markt
                      </th>
                      <th className="text-right py-3 px-2 font-medium text-gray-500" data-id="element-1358">
                        Rating
                      </th>
                      <th className="text-center py-3 px-2 font-medium text-gray-500" data-id="element-1359">
                        #
                      </th>
                      {services.slice(0, 10).map(service => {
                    const ServiceIcon = service.icon;
                    return <th key={service.id} className="text-center py-3 px-1 font-medium text-gray-500" title={service.name} data-id="element-1360">
                            <ServiceIcon size={14} className="mx-auto" data-id="element-1361" />
                          </th>;
                  })}
                    </tr>
                  </thead>
                  <tbody data-id="element-1362">
                    {marketsByServices.map(market => <tr key={market.id} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-1363">
                        <td className="py-2 px-4 font-medium text-gray-900 sticky left-0 bg-white text-xs" data-id="element-1364">
                          {market.name.replace('Lidl ', '').replace('XL ', '')}
                        </td>
                        <td className="py-2 px-2 text-right" data-id="element-1365">
                          <span className={`font-medium ${market.rating >= 4.3 ? 'text-green-600' : market.rating >= 3.8 ? 'text-gray-900' : 'text-red-600'}`} data-id="element-1366">
                            {market.rating.toFixed(1)}
                          </span>
                        </td>
                        <td className="py-2 px-2 text-center font-medium text-gray-500" data-id="element-1367">
                          {market.services.length}
                        </td>
                        {services.slice(0, 10).map(service => <td key={service.id} className="py-2 px-1 text-center" data-id="element-1368">
                            {market.services.includes(service.id) ? <CheckCircle2 size={14} className="mx-auto text-green-500" data-id="element-1369" /> : <XCircle size={14} className="mx-auto text-gray-200" data-id="element-1370" />}
                          </td>)}
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          </Card>
        </>}
    </div>;
}